import { LOCALE_ID, Pipe, PipeTransform, inject } from '@angular/core';
import { formatNumber } from '@angular/common';
import { Decimal } from 'decimal.js';

/**
 * Number
 *
 * Formats a number with grouping and decimal places, like Angular's `number`
 * pipe, and also accepts a decimal.js Decimal (the value of a Decimal-backed
 * base type such as Currency).
 */
@Pipe({
  name: 'number',
  standalone: true,
})
export class NumberPipe implements PipeTransform {
  locale: string = inject(LOCALE_ID);

  transform(
    value: Decimal | number | string | null | undefined,
    digitsInfo?: string,
    locale?: string,
  ): string | null {
    // An empty value shows nothing, as with Angular's pipe
    if (value == null || value === '') return null;

    // Text that isn't a number is a template error, as with Angular's pipe
    let amount: Decimal;
    try {
      // Read the value as an exact Decimal
      amount = new Decimal(value);
    } catch {
      // Report the value Angular's pipe would have rejected
      throw new Error(`InvalidPipeArgument: '${value}' for pipe 'NumberPipe'`);
    }

    /**
     * Round with Decimal before formatting
     *
     * Angular formats numbers, so the value becomes one only once it is
     * rounded to the digits it will show; a computed value never shows float
     * drift, and half rounds away from zero.
     */
    const rounded = amount.toDecimalPlaces(
      this.maxFractionDigits(digitsInfo),
      Decimal.ROUND_HALF_UP,
    );
    return formatNumber(rounded.toNumber(), locale || this.locale, digitsInfo);
  }

  maxFractionDigits(digitsInfo?: string): number {
    // Without a readable digitsInfo, the default stands
    const defaultMax = 3;
    const parts = digitsInfo?.match(/^(\d+)?\.((\d+)(-(\d+))?)?$/);
    if (!parts) return defaultMax;

    // An explicit maximum wins
    if (parts[5] != null) return parseInt(parts[5], 10);

    // A minimum above the default raises the maximum with it
    if (parts[3] != null) return Math.max(parseInt(parts[3], 10), defaultMax);

    // Otherwise the default stands
    return defaultMax;
  }
}
